import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  useAlert,
} from '@ads/components-react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  adaptDetailToForm,
  ClassModuleFormProvider,
  ClassModuleFormSkeleton,
  ClassModuleFormTemplate,
  ClassModuleLoadError,
  resolveModuleReturnPath,
  useClassModuleDetail,
  useSaveClassModule,
} from '@/features/class-module-management';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

const searchSchema = z.object({
  /** Quando `grade-config`, o retorno (salvar/cancelar) volta para a tela de Nota/Frequência. */
  returnTo: z.literal('grade-config').optional(),
});

export const Route = createFileRoute('/_authenticated/class-modules/$id/')({
  validateSearch: searchSchema,
  component: ClassModuleEditPage,
});

/** Página de edição de um módulo de turma existente. */
export function ClassModuleEditPage() {
  const { id } = Route.useParams();
  const { returnTo } = Route.useSearch();
  const moduleId = Number(id);
  const navigate = useNavigate();
  const { t } = useTranslation('classModuleManagement');
  const { alert } = useAlert();

  const returnPath = resolveModuleReturnPath(returnTo);

  const { data, isLoading, isError, refetch } = useClassModuleDetail(moduleId);
  const { save, isPending } = useSaveClassModule(moduleId);

  const initialValues = useMemo(() => (data ? adaptDetailToForm(data) : undefined), [data]);

  if (isLoading) {
    return <ClassModuleFormSkeleton />;
  }

  if (isError || !initialValues) {
    return (
      <div className="flex flex-col gap-xxs">
        <PageHeader
          title={t('breadcrumb.edit')}
          breadcrumb={
            <Breadcrumb>
              <BreadcrumbLink>
                <Link to="/">
                  <BreadcrumbIcon />
                </Link>
              </BreadcrumbLink>
              <BreadcrumbLink>
                <Link to="/class-modules">{t('breadcrumb.list')}</Link>
              </BreadcrumbLink>
              <BreadcrumbPage>{t('breadcrumb.edit')}</BreadcrumbPage>
            </Breadcrumb>
          }
        />
        <ClassModuleLoadError onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <ClassModuleFormProvider isEdit initialValues={initialValues}>
      <ClassModuleFormTemplate
        title={t('breadcrumb.edit')}
        isSubmitting={isPending}
        onSubmit={payload =>
          new Promise<void>((resolve, reject) => {
            save(payload, {
              onSuccess: () => resolve(),
              onError: () => {
                alert({ alertType: 'warning', title: t('toast.error') });
                reject(new Error('save failed'));
              },
            });
          })
        }
        onSaved={() => {
          alert({ alertType: 'success', title: t('toast.updateSuccess') });
          if (returnTo === 'grade-config') void navigate({ to: returnPath });
        }}
        onCancel={() => void navigate({ to: returnPath })}
      />
    </ClassModuleFormProvider>
  );
}
